import { node } from "~/util/dom";
import { _ } from "~/i18n";
import createSDSIcon from "./icon";
import { CLOSE_24 } from "@sznds/icons";

export interface IDialogOptions {
	className: string;
	classes: Array<string>;
	title: string;
	desc: string;
	bindEsc: boolean;
	bgClose: boolean;
	onEnter: (data?) => void;
	onClose?: (data?) => void;
	onClickOut?: (event: MouseEvent) => void;
	onEscape?: (event: KeyboardEvent) => void;
	logSection: string;
}

export default class Dialog {
	#opts: IDialogOptions;

	#dom: {
		container: HTMLElement;
		cover: HTMLElement;
		btnClose: HTMLElement;
		content: HTMLElement;
		buttons: HTMLElement;
		title?: HTMLElement;
		desc?: HTMLElement;
		error: HTMLParagraphElement;
	};

	#opened: boolean;

	#onEnter: () => void;

	constructor(optsArg: Partial<IDialogOptions> = {}) {
		this.#opts = {
			className: "",
			classes: [],
			title: "",
			desc: "",
			bindEsc: true, // bindovat close pro zavreni dialogu
			bgClose: true, // da se zavrit pres pozadi
			onEnter: () => {}, // pri zmacknuti klavesy enter
			onClose: () => { this.close(); }, // data: { e: event, isEsc, isCross, isBackground }
			logSection: "", // pro logovani - kazde tlacitko ma tento log
			...optsArg,
		};
		this.#dom = {
			container: node("div", { className: "mapy-dialog" }),
			cover: node("div", { className: "mapy-dialog-cover" }),
			btnClose: node("button", { className: "btn-close" }),
			content: node("div", { className: "content" }),
			buttons: node("div", { className: "buttons" }),
			error: node("p", { className: "error" }),
		};
		this.#opened = false;
		this.#onEnter = () => {};

		this.#build();
	}

	destroy() {
		this.close();
		this.#dom.container = null;
		this.#dom.cover = null;
	}

	get dom() {
		return this.#dom;
	}

	get isOpened() {
		return this.#opened;
	}

	set onClose(onClose: () => void) {
		this.#opts.onClose = onClose;
	}

	handleEvent(event) {
		switch (event.type) {
			case "drop":
				event.preventDefault();
				event.stopPropagation();
				break;
			case "keydown":
				if (event.keyCode === 13) {
					// bind z tlacitek
					this.#onEnter();
					// vlastni event
					this.#opts.onEnter(event);
				} else if (event.keyCode === 27) {
					this.#opts.onEscape?.(event);

					if (this.#opts.bindEsc) {
						this.#close(event, true);
					}
				}
				break;
			default:
				break;
		}
	}

	setError(errorMsg: string) {
		this.#dom.error.textContent = errorMsg;

		if (errorMsg) {
			this.#dom.content.appendChild(this.#dom.error);
		} else {
			this.#dom.error.remove();
		}
	}

	addBtn(optsArg: Partial<{
		title: string;
		cb: (event?) => void;
		type: string;
		bindEnter: boolean;
		logIgnore: boolean;
	}> = {}): HTMLButtonElement {
		const opts = {
			title: "",
			cb: () => {},
			type: "",
			bindEnter: false, // nabindovat cb tlacitka jako enter akci
			logIgnore: false,
			...optsArg,
		};

		const className = `btn btn-${opts.type === "" ? "default" : opts.type === "ok" ? "ok" : "error"}`;
		const btnEl = node("button", { className, textContent: opts.title });

		btnEl.addEventListener("click", event => {
			if (typeof optsArg.cb === "function") {
				opts.cb(event);
			}
		});

		this.#dom.buttons.appendChild(btnEl);

		if (this.#opts.logSection) {
			btnEl.setAttribute("data-log-section", this.#opts.logSection);
		}

		if (opts.logIgnore) {
			btnEl.setAttribute("data-log-ignore", "true");
		}

		if (opts.bindEnter) {
			this.#onEnter = opts.cb;
		}

		return btnEl;
	}

	setBasic(optsArg: Partial<{
		cb: () => void,
		closeTxt: string;
		bindEnter: boolean;
	}> = {}): {
		closeBtn: HTMLButtonElement;
		cancelBtn: HTMLButtonElement;
	} {
		const opts = {
			cb: () => {},
			closeTxt: "close",
			bindEnter: false, // tlacitko close bude nabindovano na enter
			...optsArg,
		};

		const closeBtn = this.addBtn({
			title: _(opts.closeTxt),
			type: "error",
			bindEnter: opts.bindEnter,
			cb: () => {
				this.close();
				opts.cb();
			},
		});
		const cancelBtn = this.addBtn({
			title: _("cancel"),
			cb: () => {
				this.close();
			},
		});

		return {
			closeBtn,
			cancelBtn,
		};
	}

	setSave(optsArg: Partial<{
		cb: () => void;
		bindEnter: boolean;
	}> = {}): {
		saveBtn: HTMLButtonElement;
		cancelBtn: HTMLButtonElement;
	} {
		const opts = {
			cb: () => {},
			bindEnter: false, // tlacitko ulozit bude nabindovano na enter
			...optsArg,
		};

		const saveBtn = this.addBtn({
			title: _("save"),
			type: "ok",
			bindEnter: opts.bindEnter,
			cb: () => {
				this.close();
				opts.cb();
			},
		});
		const cancelBtn = this.addBtn({
			title: _("cancel"),
			cb: () => {
				this.close();
			},
		});

		return {
			saveBtn,
			cancelBtn,
		};
	}

	removeAllButtons() {
		this.#dom.buttons.innerHTML = "";
	}

	open() {
		if (this.#opened) {
			return;
		}

		document.body.appendChild(this.#dom.cover);
		document.body.addEventListener("keydown", this);
		document.body.style.overflow = "hidden";
		this.#opened = true;
	}

	close() {
		if (!this.#opened) {
			return;
		}

		document.body.removeEventListener("keydown", this);
		document.body.style.overflow = "";
		this.#dom.cover.remove();
		this.#opened = false;
	}

	#build() {
		this.#dom.cover.appendChild(this.#dom.container);

		this.#dom.cover.addEventListener("click", event => {
			if (!this.#dom.container || this.#dom.container.contains(event.target as Node)) {
				return;
			}

			if (!event.defaultPrevented) {
				this.#opts.onClickOut?.(event);
			}

			if (this.#opts.bgClose) {
				this.#close(event, false, false, true);
			}
		});

		this.#dom.cover.addEventListener("drop", this);

		this.#dom.btnClose.appendChild(createSDSIcon({
			symbol: CLOSE_24,
		}));
		this.#dom.btnClose.setAttribute("data-log-ignore", "true");
		this.#dom.btnClose.addEventListener("click", event => {
			this.#close(event, false, true);
		});

		this.#dom.container.appendChild(this.#dom.btnClose);

		if (this.#opts.title) {
			this.#dom.title = node("h3", { className: "title", textContent: this.#opts.title });
			this.#dom.container.appendChild(this.#dom.title);
		}

		this.#dom.container.appendChild(this.#dom.content);

		if (this.#opts.desc) {
			this.#dom.desc = node("p", { className: "desc", textContent: this.#opts.desc });
			this.#dom.content.appendChild(this.#dom.desc);
		}

		this.#dom.container.appendChild(this.#dom.buttons);

		if (this.#opts.className) {
			this.#dom.container.classList.add(this.#opts.className);
		}

		this.#opts.classes.forEach(className => this.#dom.container.classList.add(className));
	}

	#close(event, isEsc = false, isCross = false, isBackground = false) {
		this.#opts.onClose?.({
			e: event,
			isEsc,
			isCross,
			isBackground,
		});
	}
}
